import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Progress } from '../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { HardDrive, DollarSign, Upload, Video, AlertTriangle, RefreshCw, Trash2, RotateCw, CheckCircle2, XCircle, Loader2, Activity } from 'lucide-react';
import { formatIST } from '../../lib/utils';
import { ObjectStorageCard } from '../../components/ObjectStorageCard';
import { toast } from 'sonner';

export default function StorageDashboard() {
  const [usage, setUsage] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [videos, setVideos] = useState([]);
  const [totalVideos, setTotalVideos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [videosLoading, setVideosLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [usageRes, pipelineRes] = await Promise.all([
        api.get('/api/bunny/usage'),
        api.get('/api/bunny/pipeline-status'),
      ]);
      setUsage(usageRes.data);
      setPipeline(pipelineRes.data);
    } catch (err) {
      toast.error('Failed to load dashboard data');
    }
    setLoading(false);
  };

  const fetchVideos = async (p = 1, filter = statusFilter) => {
    setVideosLoading(true);
    try {
      const params = { page: p, limit: 15 };
      if (filter !== 'all') params.status = filter;
      const res = await api.get('/api/bunny/videos', { params });
      setVideos(res.data.videos);
      setTotalVideos(res.data.total);
    } catch (err) {
      toast.error('Failed to load videos');
    }
    setVideosLoading(false);
  };

  useEffect(() => { fetchDashboard(); fetchVideos(); }, []);

  const handleFilterChange = (val) => {
    setStatusFilter(val);
    setPage(1);
    fetchVideos(1, val);
  };

  const handleRetry = async (jobId) => {
    try {
      const res = await api.post(`/api/bunny/retry/${jobId}`);
      const usedFresh = res.data?.used_fresh_token;
      toast.success(usedFresh ? 'Retry started with fresh Zoom token' : 'Retry started');
      // Give the pipeline a few seconds to spawn a new job, then refresh
      setTimeout(() => fetchVideos(page), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retry failed');
    }
  };

  const handleRetryAllFailed = async () => {
    if (!window.confirm('Retry every failed Bunny pipeline job? This will re-fetch fresh Zoom download tokens via the API and re-run downloads.')) return;
    try {
      const res = await api.post('/api/bunny/retry-all-failed');
      const { queued, skipped, used_fresh_zoom_api_token } = res.data || {};
      toast.success(`Queued ${queued} job(s) — ${used_fresh_zoom_api_token} got fresh tokens, ${skipped} skipped`);
      setTimeout(() => { fetchDashboard(); fetchVideos(page); }, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk retry failed');
    }
  };

  const handleDelete = async (bunnyGuid) => {
    if (!window.confirm('Delete this video from Bunny Stream? This cannot be undone.')) return;
    try {
      await api.delete(`/api/bunny/videos/${bunnyGuid}`);
      toast.success('Video deleted');
      fetchDashboard();
      fetchVideos(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  };

  const getStatusBadge = (status) => {
    const map = {
      downloading: { variant: 'outline', className: 'bg-blue-50 text-blue-700 border-blue-200', icon: Loader2, spin: true },
      uploading: { variant: 'outline', className: 'bg-amber-50 text-amber-700 border-amber-200', icon: Upload, spin: false },
      encoding: { variant: 'outline', className: 'bg-orange-50 text-orange-700 border-orange-200', icon: Loader2, spin: true },
      ready: { variant: 'outline', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2, spin: false },
      failed: { variant: 'outline', className: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle, spin: false },
      encoding_failed: { variant: 'outline', className: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle, spin: false },
      skipped_too_large: { variant: 'outline', className: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle, spin: false },
      deleted: { variant: 'outline', className: 'bg-gray-50 text-gray-500 border-gray-200', icon: Trash2, spin: false },
    };
    const s = map[status] || { variant: 'outline', className: '', icon: Activity, spin: false };
    const Icon = s.icon;
    return (
      <Badge variant={s.variant} className={`text-xs ${s.className}`}>
        <Icon className={`h-3 w-3 mr-1 ${s.spin ? 'animate-spin' : ''}`} />
        {status?.replace(/_/g, ' ')}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-5xl" data-testid="storage-dashboard-loading">
        <Skeleton className="h-8 w-72" />
        <div className="grid gap-4 md:grid-cols-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-2xl" />)}
        </div>
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  const costPercent = usage ? Math.min((usage.estimated_monthly_cost.total / usage.cost_alert_threshold) * 100, 100) : 0;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="storage-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Storage & Billing</h1>
          <p className="text-muted-foreground mt-1">Files, documents & the Bunny Stream video pipeline</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchDashboard(); fetchVideos(page); }} data-testid="refresh-dashboard">
          <RefreshCw className="h-4 w-4 mr-1.5" />Refresh
        </Button>
      </div>

      <ObjectStorageCard />

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="rounded-2xl" data-testid="kpi-total-videos">
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <Video className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Videos</p>
                <p className="text-2xl font-semibold">{usage?.total_videos || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl" data-testid="kpi-storage">
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-blue-100">
                <HardDrive className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Storage Used</p>
                <p className="text-2xl font-semibold">{usage?.total_storage_gb || 0} GB</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl" data-testid="kpi-uploads-month">
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-amber-100">
                <Upload className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">This Month</p>
                <p className="text-2xl font-semibold">{usage?.this_month_uploads || 0} <span className="text-sm font-normal text-muted-foreground">uploads</span></p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`rounded-2xl ${usage?.is_over_threshold ? 'border-rose-300 bg-rose-50/30' : ''}`} data-testid="kpi-monthly-cost">
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${usage?.is_over_threshold ? 'bg-rose-100' : 'bg-emerald-100'}`}>
                <DollarSign className={`h-5 w-5 ${usage?.is_over_threshold ? 'text-rose-600' : 'text-emerald-600'}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Est. Monthly Cost</p>
                <p className="text-2xl font-semibold">${usage?.estimated_monthly_cost?.total?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
            <div className="mt-3">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Budget: ${usage?.cost_alert_threshold || 50}</span>
                <span>{costPercent.toFixed(0)}%</span>
              </div>
              <Progress value={costPercent} className="h-1.5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost Breakdown */}
      <Card className="rounded-2xl" data-testid="cost-breakdown">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <DollarSign className="h-5 w-5" />Cost Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 rounded-xl bg-muted/30">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Storage</p>
              <p className="text-xl font-semibold mt-1">${usage?.estimated_monthly_cost?.storage?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-muted-foreground mt-1">{usage?.total_storage_gb || 0} GB @ $0.01/GB/mo</p>
            </div>
            <div className="p-4 rounded-xl bg-muted/30">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Encoding</p>
              <p className="text-xl font-semibold mt-1">${usage?.estimated_monthly_cost?.encoding?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-muted-foreground mt-1">{usage?.this_month_uploads || 0} videos this month</p>
            </div>
            <div className="p-4 rounded-xl bg-muted/30">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Pipeline Status</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {pipeline && Object.entries(pipeline.pipeline).filter(([_, v]) => v > 0).map(([status, count]) => (
                  <Badge key={status} variant="outline" className="text-xs">{status}: {count}</Badge>
                ))}
                {pipeline && pipeline.total_processed === 0 && (
                  <p className="text-xs text-muted-foreground">No videos processed yet</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Active Jobs */}
      {pipeline?.active_jobs > 0 && (
        <Card className="rounded-2xl border-amber-200 bg-amber-50/30" data-testid="active-jobs-alert">
          <CardContent className="pt-5 flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-amber-600 animate-spin" />
            <div>
              <p className="text-sm font-medium text-amber-800">{pipeline.active_jobs} active job(s) in pipeline</p>
              <p className="text-xs text-muted-foreground">Processing recordings in the background</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Videos Table */}
      <Card className="rounded-2xl" data-testid="videos-table-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Video className="h-5 w-5" />Pipeline Jobs ({totalVideos})
            </CardTitle>
            <div className="flex items-center gap-2">
              {(pipeline?.pipeline?.failed > 0 || pipeline?.pipeline?.encoding_failed > 0) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryAllFailed}
                  data-testid="retry-all-failed-btn"
                  className="gap-1.5"
                  title="Re-run every failed job using fresh Zoom download tokens from the API"
                >
                  <RotateCw className="h-3.5 w-3.5" />
                  Retry All Failed ({(pipeline?.pipeline?.failed || 0) + (pipeline?.pipeline?.encoding_failed || 0)})
                </Button>
              )}
              <Select value={statusFilter} onValueChange={handleFilterChange}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="encoding">Encoding</SelectItem>
                  <SelectItem value="uploading">Uploading</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="deleted">Deleted</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {videosLoading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-12">
              <Video className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No pipeline jobs found</p>
              <p className="text-xs text-muted-foreground mt-1">Videos will appear here after Zoom recordings are processed</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Session</TableHead>
                    <TableHead>View</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {videos.map(v => (
                    <TableRow key={v.job_id}>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">{v.session_title || 'Session'}</p>
                          <p className="text-xs text-muted-foreground">{v.batch_name || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">{v.view_type}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(v.status)}</TableCell>
                      <TableCell className="text-sm">
                        {formatBytes(v.file_size_uploaded || v.file_size_original)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {v.created_at ? formatIST(v.created_at) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {['failed', 'encoding_failed'].includes(v.status) && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleRetry(v.job_id)} title="Retry" data-testid={`retry-${v.job_id}`}>
                              <RotateCw className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {v.bunny_guid && v.status !== 'deleted' && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDelete(v.bunny_guid)} title="Delete from Bunny" data-testid={`delete-${v.job_id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalVideos > 15 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(page - 1); fetchVideos(page - 1); }}>Previous</Button>
                  <span className="text-sm text-muted-foreground self-center">Page {page} of {Math.ceil(totalVideos / 15)}</span>
                  <Button variant="outline" size="sm" disabled={page >= Math.ceil(totalVideos / 15)} onClick={() => { setPage(page + 1); fetchVideos(page + 1); }}>Next</Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Cost Control Tips */}
      <Card className="rounded-2xl" data-testid="cost-tips">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />Cost Control
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="p-3 rounded-xl bg-muted/30">
              <p className="text-sm font-medium">1080p Direct Upload</p>
              <p className="text-xs text-muted-foreground mt-0.5">Recordings are uploaded at full 1080p quality. Bunny auto-generates adaptive streams (240p to 1080p) for free — learners get the best quality their connection supports.</p>
            </div>
            <div className="p-3 rounded-xl bg-muted/30">
              <p className="text-sm font-medium">Speaker View Default</p>
              <p className="text-xs text-muted-foreground mt-0.5">Only Speaker View is uploaded by default. Gallery View can be enabled per-batch in Batch Settings for additional storage cost.</p>
            </div>
            <div className="p-3 rounded-xl bg-muted/30">
              <p className="text-sm font-medium">Zoom Auto-Delete</p>
              <p className="text-xs text-muted-foreground mt-0.5">Zoom cloud recordings are automatically trashed 48 hours after successful Bunny upload to avoid Zoom storage costs.</p>
            </div>
            <div className="p-3 rounded-xl bg-muted/30">
              <p className="text-sm font-medium">Signed URLs</p>
              <p className="text-xs text-muted-foreground mt-0.5">All playback URLs expire in 2 hours and are domain-locked to prevent unauthorized sharing or hotlinking.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
